import { gql } from '@apollo/client';
import {
  AddressFragment,
  BenefitFragment,
  BenefitRequestFragment,
  CategoryFragment,
  ClientGoalsFragment,
  CompanyDetailsFragment,
  CompanyFragment,
  CurrencyFragment,
  GroupFragment,
  OrderFragment,
  PlanFragment,
  ProductFragment,
  PromoCodeFragment,
  SettingsFragment,
  ShopCartFragment,
  UserFragment,
  VariantFragment,
} from './fragments';

// AUTH ------------------------------------------------
export const SIGN_IN = gql`
  mutation SIGN_IN($data: SignInInput!) {
    signIn(data: $data) {
      user {
        ...UserFragment
      }
      token
    }
  }
  ${UserFragment}
`;

export const SIGN_OUT = gql`
  mutation SIGN_OUT {
    signOut {
      success
    }
  }
`;

export const RESET_PASSWORD = gql`
  mutation RESET_PASSWORD($data: ResetPasswordInput) {
    resetPassword(data: $data) {
      success
      err
    }
  }
`;

export const CHANGE_PASSWORD_WITHOUT_TOKEN = gql`
  mutation CHANGE_PASSWORD_WITHOUT_TOKEN(
    $data: ChangePasswordWithoutTokenInput
  ) {
    changePasswordWithoutToken(data: $data) {
      success
      err
    }
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation CHANGE_PASSWORD($data: ChangePasswordInput) {
    changePassword(data: $data) {
      success
      err
    }
  }
`;

// USER ------------------------------------------------

export const CREATE_USER = gql`
  mutation CREATE_USER($data: CreateUserInput) {
    createUser(data: $data) {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

export const UPDATE_USER = gql`
  mutation UPDATE_USER($data: UpdateUserInput) {
    updateUser(data: $data) {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

export const CREATE_USER_ADDRESS = gql`
  mutation CREATE_USER_ADDRESS($data: CreateUserAddressInput) {
    createUserAddress(data: $data) {
      address {
        ...AddressFragment
      }
    }
  }
  ${AddressFragment}
`;

export const UPDATE_USER_ADDRESS = gql`
  mutation UPDATE_USER_ADDRESS($data: UpdateUserAddressInput) {
    updateUserAddress(data: $data) {
      address {
        ...AddressFragment
      }
    }
  }
  ${AddressFragment}
`;

// COMPANY ------------------------------------------------

export const UPDATE_COMPANY_DETAILS = gql`
  mutation UPDATE_COMPANY_DETAILS($data: UpdateCompanyDetailsInput) {
    updateCompanyDetails(data: $data) {
      ...CompanyFragment
    }
  }
  ${CompanyFragment}
`;

// S3 ------------------------------------------------

export const SIGN_S3 = gql`
  mutation SIGN_S3($data: SignS3Input!) {
    signS3(data: $data) {
      signedRequest
      url
    }
  }
`;

// CATEGORY ------------------------------------------------

export const CREATE_CATEGORY = gql`
  mutation CreateCategory($data: CreateCategoryInput) {
    createCategory(data: $data) {
      category {
        ...CategoryFragment
      }
    }
  }
  ${CategoryFragment}
`;

export const UPDATE_CATEGORY = gql`
  mutation UPDATE_CATEGORY(
    $record: UpdateOneCategoryInput!
    $filter: FilterUpdateOneCategoryInput
    $skip: Int
    $sort: SortUpdateOneCategoryInput
  ) {
    updateCategory(record: $record, filter: $filter, skip: $skip, sort: $sort) {
      recordId
    }
  }
`;

export const UPDATE_CATEGORIES_POSITIONS = gql`
  mutation UpdateCategoriesPositions($data: UpdatePositionsInput) {
    updateCategoriesPositions(data: $data) {
      success
    }
  }
`;

// PRODUCT ------------------------------------------------

export const CREATE_PRODUCT = gql`
  mutation CREATE_PRODUCT($data: CreateProductInput) {
    createProduct(data: $data) {
      ...ProductFragment
    }
  }
  ${ProductFragment}
`;

export const UPDATE_PRODUCT = gql`
  mutation UPDATE_PRODUCT($data: UpdateProductInput) {
    updateProduct(data: $data) {
      ...ProductFragment
    }
  }
  ${ProductFragment}
`;

export const USE_ORDER_PRODUCT = gql`
  mutation USE_ORDER_PRODUCT($data: UseOrderProductInput) {
    useOrderProduct(data: $data) {
      _id
    }
  }
`;

// VARIANT -----------------------------------------

export const UPDATE_VARIANT = gql`
  mutation UPDATE_VARIANT($data: UpdateVariantInput) {
    updateVariant(data: $data) {
      ...VariantFragment
    }
  }
  ${VariantFragment}
`;

export const UPDATE_VARIANT_INVENTORY = gql`
  mutation UPDATE_VARIANT_INVENTORY($data: UpdateVariantInventoryInput) {
    updateVariantInventory(data: $data) {
      ...VariantFragment
    }
  }
  ${VariantFragment}
`;

export const CREATE_VARIANT = gql`
  mutation CREATE_VARIANT($data: CreateVariantInput) {
    createVariant(data: $data) {
      ...VariantFragment
    }
  }
  ${VariantFragment}
`;

export const CANCEL_STATUS_CODE = gql`
  mutation Mutation($data: electronicCodeIdInput) {
    cancelStatusCode(data: $data) {
      _id
      status
    }
  }
`;

// PAYMENT METHOD -----------------------------------------

export const CREATE_PAYMENT_METHOD = gql`
  mutation CREATE_PAYMENT_METHOD($record: CreateOnePaymentMethodInput!) {
    createPaymentMethod(record: $record) {
      recordId
    }
  }
`;

export const UPDATE_PAYMENT_METHOD = gql`
  mutation UPDATE_PAYMENT_METHOD(
    $record: UpdateOnePaymentMethodInput!
    $filter: FilterUpdateOnePaymentMethodInput
    $skip: Int
    $sort: SortUpdateOnePaymentMethodInput
  ) {
    updatePaymentMethod(
      record: $record
      filter: $filter
      skip: $skip
      sort: $sort
    ) {
      recordId
    }
  }
`;

// TAX -----------------------------------------

export const CREATE_TAX = gql`
  mutation CREATE_TAX($record: CreateOneTaxInput!) {
    createTax(record: $record) {
      recordId
    }
  }
`;

export const UPDATE_TAX = gql`
  mutation UPDATE_TAX(
    $record: UpdateOneTaxInput!
    $filter: FilterUpdateOneTaxInput
    $skip: Int
    $sort: SortUpdateOneTaxInput
  ) {
    updateTax(record: $record, filter: $filter, skip: $skip, sort: $sort) {
      recordId
    }
  }
`;

// PROMOCODE -----------------------------------------

export const CREATE_PROMOCODE = gql`
  mutation CREATE_PROMOCODE($record: CreateOnePromoCodeInput!) {
    createPromoCode(record: $record) {
      record {
        ...PromoCodeFragment
      }
    }
  }
  ${PromoCodeFragment}
`;
export const UPDATE_PROMOCODE = gql`
  mutation UPDATE_PROMOCODE(
    $record: UpdateOnePromoCodeInput!
    $filter: FilterUpdateOnePromoCodeInput
    $sort: SortUpdateOnePromoCodeInput
    $skip: Int
  ) {
    updatePromoCode(
      record: $record
      filter: $filter
      sort: $sort
      skip: $skip
    ) {
      record {
        ...PromoCodeFragment
      }
    }
  }
  ${PromoCodeFragment}
`;

// CURRENCY -----------------------------------------

export const CREATE_CURRENCY = gql`
  mutation CREATE_CURRENCY($record: CreateOneCurrencyInput!) {
    createCurrency(record: $record) {
      record {
        ...CurrencyFragment
      }
    }
  }
  ${CurrencyFragment}
`;

export const UPDATE_CURRENCY = gql`
  mutation UPDATE_CURRENCY(
    $record: UpdateOneCurrencyInput!
    $filter: FilterUpdateOneCurrencyInput
    $sort: SortUpdateOneCurrencyInput
    $skip: Int
  ) {
    updateCurrency(record: $record, filter: $filter, sort: $sort, skip: $skip) {
      record {
        ...CurrencyFragment
      }
    }
  }
  ${CurrencyFragment}
`;

// PLAN -----------------------------------------

export const UPDATE_PLAN = gql`
  mutation UPDATE_PLAN($data: UpdatePlanInput) {
    updatePlan(data: $data) {
      ...PlanFragment
    }
  }
  ${PlanFragment}
`;

export const CREATE_PLAN = gql`
  mutation CREATE_PLAN($data: CreatePlanInput) {
    createPlan(data: $data) {
      ...PlanFragment
    }
  }
  ${PlanFragment}
`;

// SHOPCART -----------------------------------------

export const ADD_PAYROLL_ITEM_TO_SHOP_CART = gql`
  mutation ADD_PAYROLL_ITEM_TO_SHOP_CART($data: AddPayrollItemToShopCartInput) {
    addPayrollItemToShopCart(data: $data) {
      ...ShopCartFragment
    }
  }
  ${ShopCartFragment}
`;

export const ADD_ITEM_TO_SHOP_CART = gql`
  mutation ADD_ITEM_TO_SHOP_CART($data: AddItemToShopCartInput) {
    addItemToShopCart(data: $data) {
      ...ShopCartFragment
    }
  }
  ${ShopCartFragment}
`;

export const REMOVE_ITEM_FROM_SHOP_CART = gql`
  mutation REMOVE_FROM_SHOP_CART($data: RemoveItemFromShopCartInput) {
    removeItemFromShopCart(data: $data) {
      ...ShopCartFragment
    }
  }
  ${ShopCartFragment}
`;

export const CLEAN_SHOP_CART = gql`
  mutation CLEAN_SHOP_CART($data: CleanShopCartInput) {
    cleanShopCart(data: $data) {
      ...ShopCartFragment
    }
  }
  ${ShopCartFragment}
`;

export const ADD_BENEFIT_TO_SHOP_CART = gql`
  mutation AddBenefitsToShopCart($data: AddBenefitToShopCartInput) {
    addBenefitsToShopCart(data: $data) {
      _id
    }
  }
`;

export const PAY_REWARD_CLIENT_GOAL = gql`
  mutation PayClientGoal($data: PayRewardInput) {
    payRewardClientGoal(data: $data) {
      ...ClientGoalsFragment
      user {
        ...UserFragment
      }
    }
  }
  ${ClientGoalsFragment}
  ${UserFragment}
`;
// SETTINGS -----------------------------------------

export const CREATE_SETTINGS = gql`
  mutation CREATE_SETTINGS($record: CreateOneSettingsInput!) {
    createSettings(record: $record) {
      record {
        ...SettingsFragment
      }
    }
  }
  ${SettingsFragment}
`;

export const UPDATE_SETTINGS = gql`
  mutation UPDATE_SETTINGS(
    $record: UpdateOneSettingsInput!
    $filter: FilterUpdateOneSettingsInput
    $sort: SortUpdateOneSettingsInput
    $skip: Int
  ) {
    updateSettings(record: $record, filter: $filter, sort: $sort, skip: $skip) {
      record {
        ...SettingsFragment
      }
    }
  }
  ${SettingsFragment}
`;

// BENEFITS -----------------------------------------'

export const CREATE_BENEFIT = gql`
  mutation CREATE_BENEFIT($record: CreateOneBenefitInput!) {
    createBenefit(record: $record) {
      record {
        ...BenefitFragment
      }
    }
  }
  ${BenefitFragment}
`;

export const UPDATE_BENEFIT = gql`
  mutation UPDATE_BENEFIT(
    $record: UpdateOneBenefitInput!
    $filter: FilterUpdateOneBenefitInput
    $sort: SortUpdateOneBenefitInput
    $skip: Int
  ) {
    updateBenefit(record: $record, filter: $filter, sort: $sort, skip: $skip) {
      record {
        _id
      }
    }
  }
`;

// ORDERS -----------------------------------------

export const CREATE_ORDER = gql`
  mutation CREATE_ORDER($data: CreateOrderInput) {
    createOrder(data: $data) {
      ...OrderFragment
    }
  }
  ${OrderFragment}
`;

export const CANCEL_ORDER = gql`
  mutation CANCEL_ORDER($data: CancelOrderInput) {
    cancelOrder(data: $data) {
      ...OrderFragment
    }
  }
  ${OrderFragment}
`;

export const UPDATE_ORDER = gql`
  mutation UPDATE_ORDER(
    $record: UpdateOneOrderInput!
    $filter: FilterUpdateOneOrderInput
    $sort: SortUpdateOneOrderInput
    $skip: Int
  ) {
    updateOrder(record: $record, filter: $filter, sort: $sort, skip: $skip) {
      recordId
    }
  }
`;

export const MARK_ORDER_COMPLETE = gql`
  mutation MARK_ORDER_COMPLETE($data: MarkOrderCompletedInput) {
    markOrderCompleted(data: $data) {
      ...OrderFragment
    }
  }
  ${OrderFragment}
`;

export const REFUND_ORDER = gql`
  mutation RefundOrder($data: CancelOrderInput) {
    refundOrder(data: $data) {
      status
      code
    }
  }
`;

// GOALS -----------------------------------------

export const UPDATE_CLIENT_GOAL = gql`
  mutation UpdateClientGoals(
    $record: UpdateOneClientGoalsInput!
    $filter: FilterUpdateOneClientGoalsInput
    $sort: SortUpdateOneClientGoalsInput
    $skip: Int
  ) {
    updateClientGoals(
      record: $record
      filter: $filter
      sort: $sort
      skip: $skip
    ) {
      recordId
    }
  }
`;

export const CREATE_CLIENT_GOAL = gql`
  mutation CREATE_CLIENT_GOAL($data: CreateClientGoalsInput) {
    createClientGoal(data: $data) {
      ...ClientGoalsFragment
    }
  }
  ${ClientGoalsFragment}
`;

export const ADD_CLIENT_GOAL_REWARDS = gql`
  mutation AddClientGoalRewards($data: AddClientGoalRewardsInput) {
    addClientGoalRewards(data: $data) {
      ...ShopCartFragment
    }
  }
  ${ShopCartFragment}
`;

export const UPDATE_PROGRESS_CLIENT_GOAL = gql`
  mutation ProgressClientGoal($data: ProgressClientGoalInput) {
    progressClientGoal(data: $data) {
      company {
        _id
      }
      ...ClientGoalsFragment
    }
  }
  ${ClientGoalsFragment}
`;

// PERIODS -----------------------------------------
export const CREATE_PERIOD = gql`
  mutation CreatePeriod($data: CreatePeriodInput) {
    createPeriod(data: $data) {
      name
      companyDetails {
        ...CompanyDetailsFragment
      }
    }
  }
  ${CompanyDetailsFragment}
`;

// REQUEST -----------------------------------------

export const UPDATE_BENEFIT_REQUEST = gql`
  mutation UpdateBenefitRequest(
    $record: UpdateOneBenefitRequestInput!
    $skip: Int
    $sort: SortUpdateOneBenefitRequestInput
    $filter: FilterUpdateOneBenefitRequestInput
  ) {
    updateBenefitRequest(
      record: $record
      skip: $skip
      sort: $sort
      filter: $filter
    ) {
      recordId
    }
  }
`;

export const APPROVE_OR_DECLINE_BENEFIT_REQUEST = gql`
  mutation ApproveOrDeclineBenefitRequest(
    $data: ApproveOrDeclineBenefitRequestInput
  ) {
    approveOrDeclineBenefitRequest(data: $data) {
      ...BenefitRequestFragment
    }
  }
  ${BenefitRequestFragment}
`;

export const MARK_BENEFIT_REQUEST_PAID = gql`
  mutation MarkBenefitRequestPaid($data: MarkBenefitRequestPaidInput) {
    markBenefitRequestPaid(data: $data) {
      ...BenefitRequestFragment
    }
  }
  ${BenefitRequestFragment}
`;

export const PAY_PAYABLE_ACCOUNT = gql`
  mutation PayAccountByAlly($data: PayAccountAllyInput) {
    payAccountByAlly(data: $data) {
      total
      _id
      orderPayments {
        paymentStatus
      }
    }
  }
`;

export const CANCEL_PAYMENT_TDC = gql`
  mutation CancelPaymentTDC($data: PaymentIdInput) {
    cancelPaymentTDC(data: $data) {
      status
      _id
    }
  }
`;

export const ASSING_BENEFIT_TO_EMP = gql`
  mutation Mutation($data: AssignBenefitsEmployeesInput) {
    assignBenefitsEmployees(data: $data) {
      _id
      name
      email
      domain
    }
  }
`;
// UPLOADS -----------------------------------------
export const UPLOAD_COMPANY_GOAL = gql`
  mutation UploadCompanyGoals($data: UploadCompanyGoalInput) {
    uploadCompanyGoals(data: $data) {
      _id
    }
  }
`;

export const UPLOAD_USER_CLIENT = gql`
  mutation UploadUserClient($data: UploadUserClientInput) {
    uploadUserClient(data: $data) {
      success
      message
    }
  }
`;

export const UPLOAD_CLIENTS_GOALS_RESULTS = gql`
  mutation UploadClientGoalResult($data: UploadClientGoalResultInput) {
    uploadClientGoalResult(data: $data) {
      _id
    }
  }
`;
// GROUPS -----------------------------------------
export const CREATE_GROUP = gql`
  mutation CreateGroup($data: CreateGroupInput) {
    createGroup(data: $data) {
      ...GroupFragment
    }
  }
  ${GroupFragment}
`;

export const UPDATE_GROUP = gql`
  mutation Mutation($data: UpdateGroupInput) {
    updateGroup(data: $data) {
      ...GroupFragment
    }
  }
  ${GroupFragment}
`;

export const PAY_REWARD_TO_GROUP = gql`
  mutation PayRewardGroup($data: GroupIdInput) {
    payRewardGroup(data: $data) {
      _id
    }
  }
`;
